import { z } from 'zod';
import { timestampSchema } from './common';
import { entityAnchorQuerySchema, entityRefSchema } from './entities';

/**
 * Asking to be told when something changes
 * (docs/document-register-design.md section 5).
 *
 * The platform's fifth mechanism keyed by `(entityKind, entityId)`, after
 * links, revisions, comments and votes, and built the same way for the same
 * reason: what somebody wants to hear about is not a property of one module's
 * table, and a `document_follower` table would be followed by a
 * `register_item_follower` and a `schedule_task_follower` inside a year.
 *
 * ## What it is not
 *
 * Not a notification preference. `notifications.ts` holds those, and the two
 * answer different questions: a **category** is "do I want to hear about
 * documents changing at all", held once per person; a **subscription** is
 * "these four drawings, specifically". Somebody who has switched the category
 * off hears nothing however many things they follow, which is the correct
 * precedence -- the general refusal wins over the specific request, because a
 * person who turned a category off has said something about their inbox rather
 * than about a drawing.
 *
 * Not a participant list either. `ScheduleTaskParticipant` records that
 * somebody is *accountable for* a task, and their notifications follow from
 * that; this records only that they asked to watch. Nobody is subscribed by
 * somebody else.
 *
 * ## One rule the read side must keep
 *
 * A subscription is per person and never aggregated into a follower list. Who
 * else is watching a drawing is not a fact the product needs to publish, and
 * publishing it would make following a visible act rather than a private one --
 * at which point people stop doing it on documents that matter.
 */

export const entitySubscriptionSchema = z.object({
  anchor: entityRefSchema,
  /** When they started following. Shown nowhere yet; kept because "since when"
   *  is the first question asked of any list of these. */
  createdAt: timestampSchema,
});
export type EntitySubscription = z.infer<typeof entitySubscriptionSchema>;

/**
 * Whether the caller follows this item.
 *
 * A boolean rather than the row, because the row has nothing else in it the
 * caller can act on and a `null | EntitySubscription` return would make every
 * call site write the same coercion.
 */
export const subscriptionStateSchema = z.object({
  following: z.boolean(),
});
export type SubscriptionState = z.infer<typeof subscriptionStateSchema>;

/**
 * `PUT` and `DELETE` on "my subscription to this item", addressed by the anchor
 * pair -- the same address `LinksController` and the votes routes already use,
 * and for the reason stated there: `kind` and `id` are one address in two
 * parts.
 *
 * `PUT` rather than `POST`, for the reason the vote routes give: one row per
 * person per item is the invariant, so following twice is the same act twice
 * rather than a duplicate the database has to refuse.
 */
export const subscribeSchema = z.object({ anchor: entityRefSchema }).strict();
export type Subscribe = z.infer<typeof subscribeSchema>;

export const subscriptionQuerySchema = entityAnchorQuerySchema;
export type SubscriptionQuery = z.infer<typeof subscriptionQuerySchema>;
